import type { TaskListType } from '@/types/task-lists';
import type { TourStep, TourVariant } from '@gokrew/ui';
import type { EmployeeFlowType } from '@gokrew/models/employee-boarding';
import type { UIAssignmentMode } from '@/pages/tasks/Components/Details/Taskmode';

/**
 * Brings a step's target into view before Joyride positions its tooltip.
 * Joyride only reliably auto-scrolls the very first step (`scrollToFirstStep`)
 * — later steps whose target sits in a different scroll container (a nested
 * `overflow-auto` region, a two-column layout below the fold, etc.) are often
 * left partly or fully off-screen. Retries across a few frames since `onEnter`
 * can fire while the target's page/section is still mounting.
 *
 * Uses `behavior: 'auto'` (instant), not `'smooth'`: TourGuide's `before` hook
 * only waits for the target to *exist* in the DOM, not for a scroll animation
 * to finish, so a smooth/animated scroll races the tooltip's Floating UI
 * position calculation — the tooltip (and its Next/Finish controls) would get
 * positioned against wherever the target was mid-scroll, not its final
 * resting place inside the nested container.
 */
const scrollTourTargetIntoView = (selector: string, retriesLeft = 20) => {
  const el = document.querySelector(selector);
  if (el) {
    el.scrollIntoView({ behavior: 'auto', block: 'center' });
    return;
  }
  if (retriesLeft > 0) {
    requestAnimationFrame(() =>
      scrollTourTargetIntoView(selector, retriesLeft - 1)
    );
  }
};

// ─── Dashboard ─────────────────────────────────────────────────────────────────

// Each card the dashboard gates behind a PermissionGuard has to gate its step
// too: a declared step whose target never mounts leaves the tour stuck on a
// count it can't reach, and Joyride ends the run early on TARGET_NOT_FOUND.
export const buildDashboardTourVariant = ({
  hasOnLeaveCard,
  hasAttentionCard,
  hasAssetsCard,
  hasBirthdaysCard = false,
}: {
  hasOnLeaveCard: boolean;
  hasAttentionCard: boolean;
  hasAssetsCard: boolean;
  hasBirthdaysCard?: boolean;
}): TourVariant => ({
  tourId: 'dashboard',
  initialMockState: {},
  buildSteps: (_setMockState, t) => {
    const steps: TourStep[] = [
      {
        target: '[data-tour="dashboard-summary"]',
        title: t('dashboard.summary.title'),
        content: t(
          hasOnLeaveCard
            ? 'dashboard.summary.content_with_approvals'
            : 'dashboard.summary.content'
        ),
        placement: 'bottom' as const,
        skipBeacon: true,
      },
    ];

    if (hasAttentionCard) {
      steps.push({
        target: '[data-tour="dashboard-attention"]',
        title: t('dashboard.attention.title'),
        content: t('dashboard.attention.content'),
        placement: 'top' as const,
        skipBeacon: true,
      });
    }

    // The narrow column, top to bottom: Who's away, then Birthdays.
    if (hasOnLeaveCard) {
      steps.push({
        target: '[data-tour="dashboard-on-leave"]',
        title: t('dashboard.on_leave.title'),
        content: t('dashboard.on_leave.content'),
        placement: 'left' as const,
        skipBeacon: true,
      });
    }

    if (hasBirthdaysCard) {
      steps.push({
        target: '[data-tour="dashboard-birthdays"]',
        title: t('dashboard.birthdays.title'),
        content: t('dashboard.birthdays.content'),
        placement: 'left' as const,
        skipBeacon: true,
      });
    }

    if (hasAssetsCard) {
      steps.push({
        target: '[data-tour="dashboard-assets"]',
        title: t('dashboard.assets.title'),
        content: t('dashboard.assets.content'),
        placement: 'top' as const,
        skipBeacon: true,
      });
    }

    return steps;
  },
});

// ─── Employees ─────────────────────────────────────────────────────────────────

export type EmployeesTourMock = Record<string, unknown> & {
  showAddDropdown: boolean;
  showFilters: boolean;
  showMockFolder: boolean;
};

export const employeesTourVariant: TourVariant<EmployeesTourMock> = {
  tourId: 'employees',
  initialMockState: {
    showAddDropdown: false,
    showFilters: false,
    showMockFolder: false,
  },
  buildSteps: (setMockState, t) => [
    {
      target: '[data-tour="employees-header"]',
      title: t('employees.header.title'),
      content: t('employees.header.content'),
      placement: 'bottom',
      skipBeacon: true,
      onEnter: () =>
        setMockState((s) => ({
          ...s,
          showAddDropdown: false,
          showFilters: false,
          showMockEmployee: false,
          showMockFolder: false,
        })),
    },
    {
      target: '[data-tour="employees-add-dropdown"]',
      title: t('employees.add_dropdown.title'),
      content: t('employees.add_dropdown.content'),
      placement: 'bottom',
      skipBeacon: true,
      onEnter: () => setMockState((s) => ({ ...s, showAddDropdown: true })),
      onExit: () => setMockState((s) => ({ ...s, showAddDropdown: false })),
    },
    {
      target: '[data-tour="employees-search"]',
      title: t('employees.search.title'),
      content: t('employees.search.content'),
      placement: 'bottom',
      skipBeacon: true,
    },
    {
      target: '[data-tour="employees-filters-panel"]',
      title: t('employees.filters.title'),
      content: t('employees.filters.content'),
      placement: 'left',
      skipBeacon: true,
      spotlightPadding: 0,
      onEnter: () => setMockState((s) => ({ ...s, showFilters: true })),
      onExit: () => setMockState((s) => ({ ...s, showFilters: false })),
    },
    {
      target: '[data-tour="employees-table"] tbody tr:first-child',
      title: t('employees.table.title'),
      content: t('employees.table.content'),
      placement: 'bottom',
      skipBeacon: true,
    },
  ],
};

// ─── Files ─────────────────────────────────────────────────────────────────────

export type FilesTourMock = Record<string, unknown> & {
  showMockFolder: boolean;
  showMockSubFolders: boolean;
  showMockFiles: boolean;
};

export const filesTourVariant: TourVariant<FilesTourMock> = {
  tourId: 'files',
  initialMockState: {
    showMockFolder: false,
    showMockSubFolders: false,
    showMockFiles: false,
  },
  buildSteps: (setMockState, t) => [
    {
      target: 'body',
      title: t('files.intro.title'),
      content: t('files.intro.content'),
      placement: 'center',
      skipBeacon: true,
      onEnter: () =>
        setMockState((s) => ({
          ...s,
          showMockFolder: false,
          showMockSubFolders: false,
          showMockFiles: false,
        })),
    },
    {
      target: '[data-tour="files-all-files"]',
      title: t('files.all_files.title'),
      content: t('files.all_files.content'),
      placement: 'right',
      skipBeacon: true,
    },
    {
      target: '[data-tour="files-folder-tree"]',
      title: t('files.folder_tree.title'),
      content: t('files.folder_tree.content'),
      placement: 'right',
      skipBeacon: true,
      onEnter: () => setMockState((s) => ({ ...s, showMockFolder: true })),
      onExit: () => setMockState((s) => ({ ...s, showMockFolder: false })),
    },
    {
      target: '[data-tour="files-bin"]',
      title: t('files.bin.title'),
      content: t('files.bin.content'),
      placement: 'right',
      skipBeacon: true,
    },
    {
      target: '[data-tour="files-folder-header"]',
      title: t('files.folder_header.title'),
      content: t('files.folder_header.content'),
      placement: 'bottom',
      skipBeacon: true,
      onEnter: () =>
        scrollTourTargetIntoView('[data-tour="files-folder-header"]'),
    },
    {
      target: '[data-tour="files-view-toggle"]',
      title: t('files.view_toggle.title'),
      content: t('files.view_toggle.content'),
      placement: 'bottom',
      skipBeacon: true,
      onEnter: () =>
        scrollTourTargetIntoView('[data-tour="files-view-toggle"]'),
    },
    {
      target: '[data-tour="files-subfolder-list"]',
      title: t('files.subfolder_list.title'),
      content: t('files.subfolder_list.content'),
      placement: 'bottom',
      skipBeacon: true,
      onEnter: () => {
        setMockState((s) => ({ ...s, showMockSubFolders: true }));
        scrollTourTargetIntoView('[data-tour="files-subfolder-list"]');
      },
      onExit: () => setMockState((s) => ({ ...s, showMockSubFolders: false })),
    },
    {
      target: '[data-tour="files-first-item"]',
      title: t('files.table.title'),
      content: t('files.table.content'),
      placement: 'top',
      skipBeacon: true,
      onEnter: () => {
        setMockState((s) => ({ ...s, showMockFiles: true }));
        scrollTourTargetIntoView('[data-tour="files-first-item"]');
      },
      onExit: () => setMockState((s) => ({ ...s, showMockFiles: false })),
    },
  ],
};

// ─── Departments ───────────────────────────────────────────────────────────────

export type DepartmentTourMock = Record<string, unknown>;

export const departmentTourVariant: TourVariant<DepartmentTourMock> = {
  tourId: 'departments',
  initialMockState: {},
  buildSteps: (_setMockState, t) => [
    {
      target: '[data-tour="department-header"]',
      title: t('departments.header.title'),
      content: t('departments.header.content'),
      placement: 'bottom',
      skipBeacon: true,
    },
    {
      target: '[data-tour="department-search"]',
      title: t('departments.search.title'),
      content: t('departments.search.content'),
      placement: 'bottom',
      skipBeacon: true,
    },
    {
      target: '[data-tour="department-table"] tbody tr:first-child',
      title: t('departments.table.title'),
      content: t('departments.table.content'),
      placement: 'bottom',
      skipBeacon: true,
    },
  ],
};

// ─── Designations ──────────────────────────────────────────────────────────────

export type DesignationTourMock = Record<string, unknown>;

export const designationTourVariant: TourVariant<DesignationTourMock> = {
  tourId: 'designations',
  initialMockState: {},
  buildSteps: (_setMockState, t) => [
    {
      target: '[data-tour="designation-header"]',
      title: t('designations.header.title'),
      content: t('designations.header.content'),
      placement: 'bottom',
      skipBeacon: true,
    },
    {
      target: '[data-tour="designation-search"]',
      title: t('designations.search.title'),
      content: t('designations.search.content'),
      placement: 'bottom',
      skipBeacon: true,
    },
    {
      target: '[data-tour="designation-table"] tbody tr:first-child',
      title: t('designations.table.title'),
      content: t('designations.table.content'),
      placement: 'bottom',
      skipBeacon: true,
    },
  ],
};

// ─── Roles ─────────────────────────────────────────────────────────────────────
export type RolesTourMock = Record<string, unknown> & {
  showMockRole: boolean;
  showFilters: boolean;
};

export const rolesTourVariant: TourVariant<RolesTourMock> = {
  tourId: 'roles',
  initialMockState: {
    showMockRole: false,
    showFilters: false,
  },
  buildSteps: (setMockState, t) => [
    {
      target: '[data-tour="roles-header"]',
      title: t('roles.header.title'),
      content: t('roles.header.content'),
      placement: 'bottom',
      skipBeacon: true,
    },
    {
      target: '[data-tour="roles-search"]',
      title: t('roles.search.title'),
      content: t('roles.search.content'),
      placement: 'bottom',
      skipBeacon: true,
    },
    {
      target: '[data-tour="roles-filters-panel"]',
      title: t('roles.filters.title'),
      content: t('roles.filters.content'),
      placement: 'left',
      skipBeacon: true,
      spotlightPadding: 0,
      onEnter: () => setMockState((s) => ({ ...s, showFilters: true })),
      onExit: () => setMockState((s) => ({ ...s, showFilters: false })),
    },
    {
      target: '[data-tour="roles-table"] tbody tr:first-child',
      title: t('roles.table.title'),
      content: t('roles.table.content'),
      placement: 'top',
      skipBeacon: true,
      onEnter: () => setMockState((s) => ({ ...s, showMockRole: true })),
      onExit: () => setMockState((s) => ({ ...s, showMockRole: false })),
    },
  ],
};

// ─── Edit Role ─────────────────────────────────────────────────────────────────
export const editRoleTourVariant: TourVariant = {
  tourId: 'edit-role',
  initialMockState: {},
  buildSteps: (_, t) => [
    {
      target: '[data-tour="edit-role-header"]',
      title: t('edit_role.header.title'),
      content: t('edit_role.header.content'),
      placement: 'bottom',
      skipBeacon: true,
    },
    {
      target: '[data-tour="edit-role-form"]',
      title: t('edit_role.form.title'),
      content: t('edit_role.form.content'),
      placement: 'right',
      skipBeacon: true,
    },
    {
      target: '[data-tour="edit-role-permissions"] tbody tr:first-child',
      title: t('edit_role.permissions.title'),
      content: t('edit_role.permissions.content'),
      placement: 'top',
      skipBeacon: true,
    },
  ],
};

// ─── Branches ─────────────────────────────────────────────────────────────────

export type BranchTourMock = Record<string, unknown>;

export const branchTourVariant: TourVariant<BranchTourMock> = {
  tourId: 'branches',
  initialMockState: {},
  buildSteps: (_setMockState, t) => [
    {
      target: '[data-tour="branch-header"]',
      title: t('branches.header.title'),
      content: t('branches.header.content'),
      placement: 'bottom',
      skipBeacon: true,
    },
    {
      target: '[data-tour="branch-search"]',
      title: t('branches.search.title'),
      content: t('branches.search.content'),
      placement: 'bottom',
      skipBeacon: true,
    },
    {
      target: '[data-tour="branch-table"] tbody tr:first-child',
      title: t('branches.table.title'),
      content: t('branches.table.content'),
      placement: 'bottom',
      skipBeacon: true,
    },
  ],
};

// ─── Vendors ──────────────────────────────────────────────────────────────────

export type VendorTourMock = Record<string, unknown>;

export const vendorTourVariant: TourVariant<VendorTourMock> = {
  tourId: 'vendors',
  initialMockState: {},
  buildSteps: (_setMockState, t) => [
    {
      target: '[data-tour="vendors-header"]',
      title: t('vendors.header.title'),
      content: t('vendors.header.content'),
      placement: 'bottom',
      skipBeacon: true,
    },
    {
      target: '[data-tour="vendors-search"]',
      title: t('vendors.search.title'),
      content: t('vendors.search.content'),
      placement: 'bottom',
      skipBeacon: true,
    },
    {
      target: '[data-tour="vendors-table"] tbody tr:first-child',
      title: t('vendors.table.title'),
      content: t('vendors.table.content'),
      placement: 'bottom',
      skipBeacon: true,
    },
  ],
};

// ─── Department Details ────────────────────────────────────────────────────────

export type DepartmentDetailsTourMock = Record<string, unknown>;

export const departmentDetailsTourVariant: TourVariant<DepartmentDetailsTourMock> =
  {
    tourId: 'department-details',
    initialMockState: {},
    buildSteps: (_setMockState, t) => [
      {
        target: '[data-tour="dept-detail-header"]',
        title: t('department_details.header.title'),
        content: t('department_details.header.content'),
        placement: 'bottom',
        skipBeacon: true,
      },
      {
        target: '[data-tour="dept-detail-branches"]',
        title: t('department_details.branches.title'),
        content: t('department_details.branches.content'),
        placement: 'bottom',
        skipBeacon: true,
      },
      {
        target: '[data-tour="dept-detail-employees"] tbody tr:first-child',
        title: t('department_details.employees.title'),
        content: t('department_details.employees.content'),
        placement: 'bottom',
        skipBeacon: true,
      },
    ],
  };

// ─── Leave Management — Overview ──────────────────────────────────────────────

export type LeaveOverviewTourMock = {
  showFilters: boolean;
};

export const leaveOverviewTourVariant: TourVariant<LeaveOverviewTourMock> = {
  tourId: 'leave-management-overview',
  initialMockState: {
    showFilters: false,
  },
  buildSteps: (setMockState, t) => [
    {
      target: '[data-tour="leave-mgmt-tabs"]',
      title: t('leave_management.tabs.title'),
      content: t('leave_management.tabs.content'),
      placement: 'bottom' as const,
      skipBeacon: true,
    },
    {
      target: '[data-tour="leave-overview-stats"]',
      title: t('leave_management.overview.stats.title'),
      content: t('leave_management.overview.stats.content'),
      placement: 'bottom' as const,
      skipBeacon: true,
    },
    {
      target: '[data-tour="leave-overview-search"]',
      title: t('leave_management.overview.search.title'),
      content: t('leave_management.overview.search.content'),
      placement: 'bottom' as const,
      skipBeacon: true,
    },
    {
      target: '[data-tour="leave-overview-filters-panel"]',
      title: t('leave_management.overview.filters.title'),
      content: t('leave_management.overview.filters.content'),
      placement: 'left' as const,
      skipBeacon: true,
      spotlightPadding: 0,
      onEnter: () => setMockState((s) => ({ ...s, showFilters: true })),
      onExit: () => setMockState((s) => ({ ...s, showFilters: false })),
    },
    {
      target: '[data-tour="leave-overview-table"] tbody tr:first-child',
      title: t('leave_management.overview.table.title'),
      content: t('leave_management.overview.table.content'),
      placement: 'top' as const,
      skipBeacon: true,
    },
  ],
};

// ─── Leave Management — Employee Wallets ──────────────────────────────────────

export type LeaveWalletTourMock = Record<string, unknown>;

export const leaveWalletTourVariant: TourVariant<LeaveWalletTourMock> = {
  tourId: 'leave-wallets',
  initialMockState: {},
  buildSteps: (_setMockState, t) => [
    {
      target: '[data-tour="leave-wallets-header"]',
      title: t('leave_management.wallet.header.title'),
      content: t('leave_management.wallet.header.content'),
      placement: 'bottom' as const,
      skipBeacon: true,
    },
    {
      target: '[data-tour="leave-wallets-search"]',
      title: t('leave_management.wallet.search.title'),
      content: t('leave_management.wallet.search.content'),
      placement: 'bottom' as const,
      skipBeacon: true,
    },
    {
      target: '[data-tour="leave-wallets-table"] tbody tr:first-child',
      title: t('leave_management.wallet.table.title'),
      content: t('leave_management.wallet.table.content'),
      placement: 'top' as const,
      skipBeacon: true,
    },
  ],
};

// ─── Leave Management — Grant Leave History ───────────────────────────────────

export type GrantLeaveHistoryTourMock = Record<string, unknown>;

export const grantLeaveHistoryTourVariant: TourVariant<GrantLeaveHistoryTourMock> =
  {
    tourId: 'grant-leave-history',
    initialMockState: { showMockGrants: false },
    buildSteps: (setMockState, t) => [
      {
        target: '[data-tour="grant-leave-header"]',
        title: t('leave_management.grant_leave.header.title'),
        content: t('leave_management.grant_leave.header.content'),
        placement: 'bottom' as const,
        skipBeacon: true,
      },
      {
        target: '[data-tour="grant-leave-btn"]',
        title: t('leave_management.grant_leave.grant_btn.title'),
        content: t('leave_management.grant_leave.grant_btn.content'),
        placement: 'left' as const,
        skipBeacon: true,
      },
      {
        target: '[data-tour="grant-leave-table"]',
        title: t('leave_management.grant_leave.table.title'),
        content: t('leave_management.grant_leave.table.content'),
        placement: 'top' as const,
        skipBeacon: true,
        onEnter: () => setMockState((s) => ({ ...s, showMockGrants: true })),
        onExit: () => setMockState((s) => ({ ...s, showMockGrants: false })),
      },
    ],
  };

// ─── Leave Management — Policies ─────────────────────────────────────────────

export type LeavePoliciesTourMock = Record<string, unknown> & {
  showMockPolicies: boolean;
};

export const leavePoliciesVariant: TourVariant<LeavePoliciesTourMock> = {
  tourId: 'leave-policies',
  initialMockState: { showMockPolicies: false },
  buildSteps: (setMockState, t) => [
    {
      target: '[data-tour="policies-search"]',
      title: t('leave_management.policies.header.title'),
      content: t('leave_management.policies.header.content'),
      placement: 'bottom' as const,
      skipBeacon: true,
    },
    {
      target: '[data-tour="policies-create-btn"]',
      title: t('leave_management.policies.create_btn.title'),
      content: t('leave_management.policies.create_btn.content'),
      placement: 'left' as const,
      skipBeacon: true,
    },
    {
      target: '[data-tour="policies-grid"] > :first-child',
      title: t('leave_management.policies.grid.title'),
      content: t('leave_management.policies.grid.content'),
      placement: 'top' as const,
      skipBeacon: true,
      onEnter: () => setMockState((s) => ({ ...s, showMockPolicies: true })),
      onExit: () => setMockState((s) => ({ ...s, showMockPolicies: false })),
    },
  ],
};

// ─── Company Admin ─────────────────────────────────────────────────────────────

export type CompanyAdminTourMock = Record<string, unknown> & {
  showMockAdmin: boolean;
  showFilters: boolean;
};

export const companyAdminTourVariant: TourVariant<CompanyAdminTourMock> = {
  tourId: 'company-admin',
  initialMockState: { showMockAdmin: false, showFilters: false },
  buildSteps: (setMockState, t) => [
    {
      target: '[data-tour="company-admin-header"]',
      title: t('company_admin.header.title'),
      content: t('company_admin.header.content'),
      placement: 'bottom',
      skipBeacon: true,
    },
    {
      target: '[data-tour="company-admin-search"]',
      title: t('company_admin.search.title'),
      content: t('company_admin.search.content'),
      placement: 'bottom',
      skipBeacon: true,
    },
    {
      target: '[data-tour="company-admin-filters-panel"]',
      title: t('company_admin.filters.title'),
      content: t('company_admin.filters.content'),
      placement: 'left',
      skipBeacon: true,
      spotlightPadding: 0,
      onEnter: () => setMockState((s) => ({ ...s, showFilters: true })),
      onExit: () => setMockState((s) => ({ ...s, showFilters: false })),
    },
    {
      target: '[data-tour="company-admin-table"] tbody tr:first-child',
      title: t('company_admin.table.title'),
      content: t('company_admin.table.content'),
      placement: 'top',
      skipBeacon: true,
      onEnter: () => setMockState((s) => ({ ...s, showMockAdmin: true })),
      onExit: () => setMockState((s) => ({ ...s, showMockAdmin: false })),
    },
  ],
};

// ─── Company Admin Details (multi-route) ───────────────────────────────────────

export type CompanyAdminDetailTourMock = Record<string, unknown> & {
  showMockOnboarding: boolean;
  showMockOffboarding: boolean;
  showMockSingleTasks: boolean;
};

/**
 * Factory so the steps can close over `navigate` and `basePath` to drive
 * tab-to-tab navigation during the tour, the same pattern as the employee
 * details tour below.
 */
export const buildCompanyAdminDetailTourVariant = (
  navigate: (path: string) => void,
  basePath: string
): TourVariant<CompanyAdminDetailTourMock> => ({
  tourId: 'company-admin-detail',
  initialMockState: {
    showMockOnboarding: false,
    showMockOffboarding: false,
    showMockSingleTasks: false,
  },
  onFinish: (startPath, finalize) => {
    navigate(startPath);
    finalize();
  },
  buildSteps: (setMockState, t) => [
    {
      target: '[data-tour="company-admin-detail-header"]',
      title: t('company_admin_detail.header.title'),
      content: t('company_admin_detail.header.content'),
      placement: 'bottom',
      skipBeacon: true,
      onEnter: () => navigate(`${basePath}/profile`),
    },
    {
      target: '[data-tour="company-admin-detail-tabs"]',
      title: t('company_admin_detail.tabs.title'),
      content: t('company_admin_detail.tabs.content'),
      placement: 'bottom',
      skipBeacon: true,
    },
    {
      target: '[data-tour="company-admin-detail-profile"]',
      title: t('company_admin_detail.profile.title'),
      content: t('company_admin_detail.profile.content'),
      placement: 'top',
      skipBeacon: true,
    },
    {
      target: 'body',
      placement: 'center',
      skipBeacon: true,
      title: t('company_admin_detail.onboarding_intro.title'),
      content: t('company_admin_detail.onboarding_intro.content'),
      onEnter: () => {
        navigate(`${basePath}/onboarding`);
        setMockState((s) => ({ ...s, showMockOnboarding: true }));
      },
    },
    {
      target: '[data-tour="company-admin-detail-task-summary"]',
      title: t('company_admin_detail.task_summary.title'),
      content: t('company_admin_detail.task_summary.content'),
      placement: 'bottom',
      skipBeacon: true,
    },
    {
      target: '[data-tour="company-admin-detail-onboarding"] li:first-child',
      title: t('company_admin_detail.onboarding.title'),
      content: t('company_admin_detail.onboarding.content'),
      placement: 'top',
      skipBeacon: true,
      onExit: () => setMockState((s) => ({ ...s, showMockOnboarding: false })),
    },
    {
      target: 'body',
      placement: 'center',
      skipBeacon: true,
      title: t('company_admin_detail.offboarding_intro.title'),
      content: t('company_admin_detail.offboarding_intro.content'),
      onEnter: () => {
        navigate(`${basePath}/offboarding`);
        setMockState((s) => ({ ...s, showMockOffboarding: true }));
      },
    },
    {
      target: '[data-tour="company-admin-detail-offboarding"] li:first-child',
      title: t('company_admin_detail.offboarding.title'),
      content: t('company_admin_detail.offboarding.content'),
      placement: 'top',
      skipBeacon: true,
      onExit: () => setMockState((s) => ({ ...s, showMockOffboarding: false })),
    },
    {
      target: 'body',
      placement: 'center',
      skipBeacon: true,
      title: t('company_admin_detail.single_tasks_intro.title'),
      content: t('company_admin_detail.single_tasks_intro.content'),
      onEnter: () => {
        navigate(`${basePath}/single_tasks`);
        setMockState((s) => ({ ...s, showMockSingleTasks: true }));
      },
    },
    {
      target: '[data-tour="company-admin-detail-single-tasks"] li:first-child',
      title: t('company_admin_detail.single_tasks.title'),
      content: t('company_admin_detail.single_tasks.content'),
      placement: 'top',
      skipBeacon: true,
      onExit: () => setMockState((s) => ({ ...s, showMockSingleTasks: false })),
    },
    {
      target: 'body',
      placement: 'center',
      skipBeacon: true,
      title: t('company_admin_detail.members_intro.title'),
      content: t('company_admin_detail.members_intro.content'),
      onEnter: () => navigate(`${basePath}/members`),
    },
    {
      target: '[data-tour="company-admin-detail-members"]',
      title: t('company_admin_detail.members.title'),
      content: t('company_admin_detail.members.content'),
      placement: 'top',
      skipBeacon: true,
    },
  ],
});

// ─── Employee Details (multi-route) ────────────────────────────────────────────

/**
 * Factory so the steps can close over `navigate` and `basePath` to drive
 * tab-to-tab navigation during the tour without touching the shared TourVariant
 * type signature.
 */
export const buildEmployeeDetailsTourVariant = (
  navigate: (path: string) => void,
  basePath: string
): TourVariant => ({
  tourId: 'employee-details',
  initialMockState: {},
  onFinish: (startPath, finalize) => {
    navigate(startPath);
    finalize();
  },
  buildSteps: (_, t) => {
    // Helper for cross-tab steps: target body so Joyride finds it instantly
    // while React renders the new outlet in the background.
    const tab = (tab: string, titleKey: string, contentKey: string) => ({
      target: 'body' as const,
      placement: 'center' as const,
      skipBeacon: true,
      title: t(titleKey),
      content: t(contentKey),
      onEnter: () => navigate(`${basePath}/${tab}`),
    });

    return [
      {
        target: '[data-tour="employee-detail-tabs"]',
        title: t('employee_detail.tabs.title'),
        content: t('employee_detail.tabs.content'),
        placement: 'bottom',
        skipBeacon: true,
        onEnter: () => navigate(`${basePath}/info`),
      },
      {
        target: '[data-tour="employee-profile-card"]',
        title: t('employee_detail.profile.title'),
        content: t('employee_detail.profile.content'),
        placement: 'right',
        skipBeacon: true,
      },
      {
        target: '[data-tour="employee-work-card"]',
        title: t('employee_detail.work_info.title'),
        content: t('employee_detail.work_info.content'),
        placement: 'left',
        skipBeacon: true,
      },
      {
        target: '[data-tour="employee-address-card"]',
        title: t('employee_detail.address.title'),
        content: t('employee_detail.address.content'),
        placement: 'top',
        skipBeacon: true,
      },
      tab(
        'education',
        'employee_detail.education.title',
        'employee_detail.education.content'
      ),
      tab(
        'documents',
        'employee_detail.documents.title',
        'employee_detail.documents.content'
      ),
      tab(
        'history',
        'employee_detail.leave_history.title',
        'employee_detail.leave_history.content'
      ),
      tab(
        'family',
        'employee_detail.family.title',
        'employee_detail.family.content'
      ),
      tab(
        'assets',
        'employee_detail.assets.title',
        'employee_detail.assets.content'
      ),
      tab(
        'notes',
        'employee_detail.notes.title',
        'employee_detail.notes.content'
      ),
      tab('bank', 'employee_detail.bank.title', 'employee_detail.bank.content'),
      tab(
        'onboarding',
        'employee_detail.onboarding.title',
        'employee_detail.onboarding.content'
      ),
      tab(
        'offboarding',
        'employee_detail.offboarding.title',
        'employee_detail.offboarding.content'
      ),
    ];
  },
});

// ─── Leave Management — Holidays ─────────────────────────────────────────────

export type HolidaysTourMock = Record<string, unknown>;

export const holidaysTourVariant: TourVariant<HolidaysTourMock> = {
  tourId: 'holidays',
  initialMockState: {},
  buildSteps: (_setMockState, t) => [
    {
      target: '[data-tour="holidays-header"]',
      title: t('leave_management.holidays.header.title'),
      content: t('leave_management.holidays.header.content'),
      placement: 'bottom' as const,
      skipBeacon: true,
    },
    {
      target: '[data-tour="holidays-year-filter"]',
      title: t('leave_management.holidays.year_filter.title'),
      content: t('leave_management.holidays.year_filter.content'),
      placement: 'bottom' as const,
      skipBeacon: true,
    },
    {
      target: '[data-tour="holidays-add-btn"]',
      title: t('leave_management.holidays.add_btn.title'),
      content: t('leave_management.holidays.add_btn.content'),
      placement: 'left' as const,
      skipBeacon: true,
    },
    {
      target: '[data-tour="holidays-table"] tbody tr:first-child',
      title: t('leave_management.holidays.table.title'),
      content: t('leave_management.holidays.table.content'),
      placement: 'top' as const,
      skipBeacon: true,
    },
  ],
};

// ─── Leave Management — Calendar ─────────────────────────────────────────────

export type CalendarTourMock = {
  showMockCalendar: boolean;
};

export const calendarTourVariant: TourVariant<CalendarTourMock> = {
  tourId: 'leave-calendar',
  initialMockState: { showMockCalendar: false },
  buildSteps: (setMockState, t) => [
    {
      target: '[data-tour="calendar-header"]',
      title: t('leave_management.calendar.header.title'),
      content: t('leave_management.calendar.header.content'),
      placement: 'bottom' as const,
      skipBeacon: true,
    },
    {
      target: '[data-tour="calendar-filters"]',
      title: t('leave_management.calendar.filters.title'),
      content: t('leave_management.calendar.filters.content'),
      placement: 'bottom' as const,
      skipBeacon: true,
    },
    {
      // The grid/timeline region inside the shared calendar — the page can't
      // wrap it, and this class is present in both the month and team views.
      target: '.leave-calendar-view',
      title: t('leave_management.calendar.view.title'),
      content: t('leave_management.calendar.view.content'),
      placement: 'top' as const,
      skipBeacon: true,
      onEnter: () => setMockState((s) => ({ ...s, showMockCalendar: true })),
    },
    {
      target: '[data-tour="calendar-legend"]',
      title: t('leave_management.calendar.legend.title'),
      content: t('leave_management.calendar.legend.content'),
      placement: 'top' as const,
      skipBeacon: true,
      onExit: () => setMockState((s) => ({ ...s, showMockCalendar: false })),
    },
  ],
};

// ─── Timesheet — Employee view ────────────────────────────────────────────────

export type TimesheetEmployeeTourMock = {
  showMockEmployees: boolean;
  showFilters: boolean;
};

export const timesheetEmployeeTourVariant: TourVariant<TimesheetEmployeeTourMock> =
  {
    tourId: 'timesheet-employee',
    initialMockState: { showMockEmployees: false, showFilters: false },
    buildSteps: (setMockState, t) => [
      {
        target: '[data-tour="timesheet-employee-search"]',
        title: t('timesheet_employee.search.title'),
        content: t('timesheet_employee.search.content'),
        placement: 'bottom' as const,
        skipBeacon: true,
      },
      {
        target: '[data-tour="timesheet-employee-week-nav"]',
        title: t('timesheet_employee.week_nav.title'),
        content: t('timesheet_employee.week_nav.content'),
        placement: 'bottom' as const,
        skipBeacon: true,
      },
      {
        target: '[data-tour="timesheet-employee-filters-panel"]',
        title: t('timesheet_employee.filters.title'),
        content: t('timesheet_employee.filters.content'),
        placement: 'left' as const,
        skipBeacon: true,
        spotlightPadding: 0,
        onEnter: () => setMockState((s) => ({ ...s, showFilters: true })),
        onExit: () => setMockState((s) => ({ ...s, showFilters: false })),
      },
      {
        target: '[data-tour="timesheet-employee-export"]',
        title: t('timesheet_employee.export.title'),
        content: t('timesheet_employee.export.content'),
        placement: 'left' as const,
        skipBeacon: true,
      },
      {
        target: '[data-tour="timesheet-employee-legend"]',
        title: t('timesheet_employee.legend.title'),
        content: t('timesheet_employee.legend.content'),
        placement: 'bottom' as const,
        skipBeacon: true,
        onEnter: () => setMockState((s) => ({ ...s, showMockEmployees: true })),
      },
      {
        target: '[data-tour="timesheet-employee-table"]',
        title: t('timesheet_employee.table.title'),
        content: t('timesheet_employee.table.content'),
        placement: 'top' as const,
        skipBeacon: true,
      },
      {
        target: '[data-tour="timesheet-employee-summary"]',
        title: t('timesheet_employee.summary.title'),
        content: t('timesheet_employee.summary.content'),
        placement: 'left' as const,
        skipBeacon: true,
        onExit: () => setMockState((s) => ({ ...s, showMockEmployees: false })),
      },
    ],
  };

// ─── Timesheet — Details Modal ────────────────────────────────────────────────

export const timesheetDetailsModalTourVariant: TourVariant = {
  tourId: 'timesheet-details-modal',
  initialMockState: {},
  buildSteps: (_, t) => [
    {
      target: '[data-tour="ts-details-employee"]',
      title: t('timesheet_details_modal.employee.title'),
      content: t('timesheet_details_modal.employee.content'),
      placement: 'bottom' as const,
      skipBeacon: true,
    },
    {
      target: '[data-tour="ts-details-stats"]',
      title: t('timesheet_details_modal.stats.title'),
      content: t('timesheet_details_modal.stats.content'),
      placement: 'bottom' as const,
      skipBeacon: true,
    },
    {
      target: '[data-tour="ts-details-actions"]',
      title: t('timesheet_details_modal.actions.title'),
      content: t('timesheet_details_modal.actions.content'),
      placement: 'bottom' as const,
      skipBeacon: true,
    },
    {
      target: '[data-tour="ts-details-day-row"]',
      title: t('timesheet_details_modal.table.title'),
      content: t('timesheet_details_modal.table.content'),
      placement: 'top' as const,
      skipBeacon: true,
    },
    {
      target: '[data-tour="ts-details-seg-actions"]',
      title: t('timesheet_details_modal.seg_actions.title'),
      content: t('timesheet_details_modal.seg_actions.content'),
      placement: 'left' as const,
      skipBeacon: true,
    },
  ],
};

// ─── Leave Management — Policy Form ───────────────────────────────────────────

export type PolicyFormTourMock = {
  entitlementDialogId: string | null;
  showMockEntitlement: boolean;
};

/**
 * Factory so the step can drive the entitlement dialog — a global dialog-context
 * portal, not inline markup — open on entering the step and closed on leaving it.
 */
export const buildPolicyFormTourVariant = (
  openEntitlementDialog: () => string,
  closeEntitlementDialog: (dialogId: string) => void
): TourVariant<PolicyFormTourMock> => ({
  tourId: 'leave-policy-form',
  initialMockState: { entitlementDialogId: null, showMockEntitlement: false },
  buildSteps: (setMockState, t) => [
    {
      target: '[data-tour="policy-form-header"]',
      title: t('leave_management.policies.modal.header.title'),
      content: t('leave_management.policies.modal.header.content'),
      placement: 'bottom' as const,
      skipBeacon: true,
    },
    {
      target: '[data-tour="policy-form-details"]',
      title: t('leave_management.policies.modal.details.title'),
      content: t('leave_management.policies.modal.details.content'),
      placement: 'right' as const,
      skipBeacon: true,
    },
    {
      target: '[data-tour="policy-form-timeline"]',
      title: t('leave_management.policies.modal.timeline.title'),
      content: t('leave_management.policies.modal.timeline.content'),
      placement: 'right' as const,
      skipBeacon: true,
    },
    {
      target: '[data-tour="policy-form-target"]',
      title: t('leave_management.policies.modal.target.title'),
      content: t('leave_management.policies.modal.target.content'),
      placement: 'left' as const,
      skipBeacon: true,
    },
    {
      target: '[data-tour="policy-form-entitlements"]',
      title: t('leave_management.policies.modal.entitlements.title'),
      content: t('leave_management.policies.modal.entitlements.content'),
      placement: 'right' as const,
      skipBeacon: true,
    },
    // The entitlement dialog is a portal mounted on the next render tick, so this
    // step targets `body`/`center` (see CLAUDE.md cross-navigation pattern) instead
    // of a field inside the not-yet-mounted dialog — avoids a stuck TARGET_NOT_FOUND.
    {
      target: 'body' as const,
      placement: 'center' as const,
      skipBeacon: true,
      title: t(
        'leave_management.policies.modal.entitlement_form.details.title'
      ),
      content: t(
        'leave_management.policies.modal.entitlement_form.details.content'
      ),
      onEnter: () => {
        const dialogId = openEntitlementDialog();
        setMockState((s) => ({ ...s, entitlementDialogId: dialogId }));
      },
      onExit: () =>
        setMockState((s) => {
          if (s.entitlementDialogId)
            closeEntitlementDialog(s.entitlementDialogId);
          return { ...s, entitlementDialogId: null, showMockEntitlement: true };
        }),
    },
    {
      target: '[data-tour="policy-form-entitlements-result"]',
      title: t('leave_management.policies.modal.entitlement_form.result.title'),
      content: t(
        'leave_management.policies.modal.entitlement_form.result.content'
      ),
      placement: 'right' as const,
      skipBeacon: true,
      onExit: () => setMockState((s) => ({ ...s, showMockEntitlement: false })),
    },
  ],
});

// ─── Mock data (single consolidated re-export) ────────────────────────────────
// ─── Asset Types (cross-route) ───────────────────────────────────────────────

/**
 * Walks the user from the Assets page out to Asset Types and back, explaining
 * how an asset type's field configuration drives the create-asset form.
 *
 * This tour crosses top-level routes (`/assets` → `/asset-types` →
 * `/asset-types/:id/settings` → `/assets/create`), so its run state is hosted
 * above page level by `AssetTourProvider`. Cross-route steps target `body` /
 * `center` with an `onEnter` navigate so Joyride resolves instantly while the
 * new route mounts; only same-page steps use `data-tour` selectors.
 *
 * `demoTypeId` is the real company asset type to open in the settings/create
 * steps, or the tour mock sentinel when the company has none.
 *
 * `confirmDiscardIfDirty` gates the finish navigation: the last step lands on
 * the create-asset form, and finishing (or skipping) the tour there would
 * otherwise navigate away and silently drop whatever the user typed. Called
 * with the navigate call as a callback — it runs it immediately when the form
 * has no unsaved data, or after the user confirms discarding it. `finalize`
 * (which flips `isTourActive` off and lets the create-form reinitialize) is
 * threaded through the same callback so it only fires once the navigate
 * actually happens — cancelling the dialog leaves the running tour, and the
 * form's unsaved values, untouched.
 */
export const buildAssetTypesTourVariant = (
  navigate: (path: string) => void,
  demoTypeId: string,
  confirmDiscardIfDirty: (onProceed: () => void) => void = (onProceed) =>
    onProceed()
): TourVariant => ({
  tourId: 'asset-types',
  initialMockState: {},
  onFinish: (startPath, finalize) =>
    confirmDiscardIfDirty(() => {
      navigate(startPath);
      finalize();
    }),
  buildSteps: (_setMockState, t) => [
    {
      target: '[data-tour="assets-header"]',
      title: t('asset_types.intro.title'),
      content: t('asset_types.intro.content'),
      placement: 'bottom',
      skipBeacon: true,
    },
    {
      target: 'body' as const,
      placement: 'center' as const,
      skipBeacon: true,
      title: t('asset_types.navigate_types.title'),
      content: t('asset_types.navigate_types.content'),
      onEnter: () => navigate('/asset-types'),
    },
    {
      target: '[data-tour="asset-types-table"] tbody tr:first-child',
      title: t('asset_types.table.title'),
      content: t('asset_types.table.content'),
      placement: 'top',
      skipBeacon: true,
    },
    {
      target: 'body' as const,
      placement: 'center' as const,
      skipBeacon: true,
      title: t('asset_types.open_settings.title'),
      content: t('asset_types.open_settings.content'),
      onEnter: () => navigate(`/asset-types/${demoTypeId}/settings`),
    },
    // The settings page is a two-column layout whose cards often sit below the
    // fold: scroll each target to center on enter and let Joyride's collision
    // detection pick a placement that fits the viewport.
    {
      target: '[data-tour="asset-type-settings-fieldset"]',
      title: t('asset_types.settings_fieldset.title'),
      content: t('asset_types.settings_fieldset.content'),
      placement: 'auto',
      skipBeacon: true,
      onEnter: () =>
        scrollTourTargetIntoView('[data-tour="asset-type-settings-fieldset"]'),
    },
    {
      target: '[data-tour="asset-type-settings-fields"]',
      title: t('asset_types.settings_fields.title'),
      content: t('asset_types.settings_fields.content'),
      placement: 'auto',
      skipBeacon: true,
      onEnter: () =>
        scrollTourTargetIntoView('[data-tour="asset-type-settings-fields"]'),
    },
    {
      target: '[data-tour="asset-type-settings-preview"]',
      title: t('asset_types.settings_preview.title'),
      content: t('asset_types.settings_preview.content'),
      placement: 'auto',
      skipBeacon: true,
      onEnter: () =>
        scrollTourTargetIntoView('[data-tour="asset-type-settings-preview"]'),
    },
    {
      target: 'body' as const,
      placement: 'center' as const,
      skipBeacon: true,
      title: t('asset_types.back_to_assets.title'),
      content: t('asset_types.back_to_assets.content'),
      onEnter: () => navigate('/assets'),
    },
    {
      target: '[data-tour="assets-table"] tbody tr:first-child',
      title: t('asset_types.assets_table.title'),
      content: t('asset_types.assets_table.content'),
      placement: 'top',
      skipBeacon: true,
    },
    {
      target: 'body' as const,
      placement: 'center' as const,
      skipBeacon: true,
      title: t('asset_types.open_form.title'),
      content: t('asset_types.open_form.content'),
      onEnter: () => navigate('/assets/create'),
    },
    {
      target: '[data-tour="asset-form-basic-info"]',
      title: t('asset_types.form_basic.title'),
      content: t('asset_types.form_basic.content'),
      placement: 'top',
      skipBeacon: true,
      onEnter: () =>
        scrollTourTargetIntoView('[data-tour="asset-form-basic-info"]'),
    },
    {
      target: '[data-tour="asset-form-asset-details"]',
      title: t('asset_types.form_asset_details.title'),
      content: t('asset_types.form_asset_details.content'),
      placement: 'top',
      skipBeacon: true,
      onEnter: () =>
        scrollTourTargetIntoView('[data-tour="asset-form-asset-details"]'),
    },
    {
      target: '[data-tour="asset-form-unit-details"]',
      title: t('asset_types.form_unit_details.title'),
      content: t('asset_types.form_unit_details.content'),
      placement: 'top',
      skipBeacon: true,
      onEnter: () =>
        scrollTourTargetIntoView('[data-tour="asset-form-unit-details"]'),
    },
    {
      target: 'body' as const,
      placement: 'center' as const,
      skipBeacon: true,
      title: t('asset_types.complete.title'),
      content: t('asset_types.complete.content'),
    },
  ],
});

// A focused tour covering only the Asset Types list and a type's settings —
// shown when the tour is triggered from the Asset Types page. It always walks
// System types → Company types → type settings; the page auto-switches tabs and
// injects mock rows per section (see AssetTypesPage). The full
// buildAssetTypesTourVariant above (Assets → Asset Types → settings → create
// form) is used when triggered from the Assets page.
export const buildAssetTypeSettingsTourVariant = (
  navigate: (path: string) => void,
  demoTypeId: string
): TourVariant => ({
  tourId: 'asset-type-settings',
  initialMockState: {},
  onFinish: (startPath, finalize) => {
    navigate(startPath);
    finalize();
  },
  buildSteps: (_setMockState, t) => [
    {
      // System types — the page opens this tab (mock row if empty) and spotlights
      // its first row.
      target: '[data-tour="asset-types-system-table"] tbody tr:first-child',
      title: t('asset_types.system_types.title'),
      content: t('asset_types.system_types.content'),
      placement: 'top',
      skipBeacon: true,
    },
    {
      // Company types — the page switches to this tab (mock row if empty) and
      // spotlights its first row.
      target: '[data-tour="asset-types-table"] tbody tr:first-child',
      title: t('asset_types.company_types.title'),
      content: t('asset_types.company_types.content'),
      placement: 'top',
      skipBeacon: true,
    },
    {
      target: 'body' as const,
      placement: 'center' as const,
      skipBeacon: true,
      title: t('asset_types.open_settings.title'),
      content: t('asset_types.open_settings.content'),
      onEnter: () => navigate(`/asset-types/${demoTypeId}/settings`),
    },
    // The settings page is a two-column layout whose cards often sit below the
    // fold: scroll each target to center on enter and let Joyride's collision
    // detection pick a placement that fits the viewport.
    {
      target: '[data-tour="asset-type-settings-fieldset"]',
      title: t('asset_types.settings_fieldset.title'),
      content: t('asset_types.settings_fieldset.content'),
      placement: 'auto',
      skipBeacon: true,
      onEnter: () =>
        scrollTourTargetIntoView('[data-tour="asset-type-settings-fieldset"]'),
    },
    {
      target: '[data-tour="asset-type-settings-fields"]',
      title: t('asset_types.settings_fields.title'),
      content: t('asset_types.settings_fields.content'),
      placement: 'auto',
      skipBeacon: true,
      onEnter: () =>
        scrollTourTargetIntoView('[data-tour="asset-type-settings-fields"]'),
    },
    {
      target: '[data-tour="asset-type-settings-preview"]',
      title: t('asset_types.settings_preview.title'),
      content: t('asset_types.settings_preview.content'),
      placement: 'auto',
      skipBeacon: true,
      onEnter: () =>
        scrollTourTargetIntoView('[data-tour="asset-type-settings-preview"]'),
    },
  ],
});

// Factories, not constants — each takes `t` (TranslationNS.Tour) so mock content
// renders in the active locale instead of being hardcoded to English.
export {
  type TourMockT,
  getMockBranch,
  getMockEmployee,
  getMockVendor,
  getMockLeaveRequest,
  getMockDepartment,
  getMockDesignation,
  getMockDeptDetailEmployee,
  getMockPolicies,
  getMockLeaveWallets,
  getMockLeaveWalletTypes,
  getMockGrantLeaveHistory,
  getMockHolidays,
  getMockCalendarEvents,
  getMockCalendarLegends,
  getMockTimesheetEmployees,
  getMockEntitlement,
  getMockRole,
  getMockCompanyAdmin,
  getMockNote,
  getMockAnnouncement,
} from './tourMocks';

// ─── Task Lists (index page) ───────────────────────────────────────────────────
// Spans all 3 Task Management tabs — onboarding + offboarding workflows, then
// the Tasks tab (one-off/recurring company tasks assigned directly to people).
export type TaskListsTourMock = Record<string, unknown> & {
  showMockTaskList: boolean;
  showMockTask: boolean;
  showFilters: boolean;
};

export const buildTaskListsTourVariant = (
  switchTab: (tab: TaskListType | 'tasks') => void
): TourVariant<TaskListsTourMock> => ({
  tourId: 'task-lists',
  initialMockState: {
    showMockTaskList: false,
    showMockTask: false,
    showFilters: false,
  },
  buildSteps: (setMockState, t) => [
    {
      target: '[data-tour="task-lists-header"]',
      title: t('task_lists.header.title'),
      content: t('task_lists.header.content'),
      placement: 'bottom',
      skipBeacon: true,
      onEnter: () => switchTab('onboarding'),
    },
    {
      target: '[data-tour="task-lists-search"]',
      title: t('task_lists.search.title'),
      content: t('task_lists.search.content'),
      placement: 'bottom',
      skipBeacon: true,
    },
    {
      target: '[data-tour="task-lists-filters-panel"]',
      title: t('task_lists.filters.title'),
      content: t('task_lists.filters.content'),
      placement: 'left',
      skipBeacon: true,
      spotlightPadding: 0,
      onEnter: () => setMockState((s) => ({ ...s, showFilters: true })),
      onExit: () => setMockState((s) => ({ ...s, showFilters: false })),
    },
    {
      target: '[data-tour="task-lists-table"] tbody tr:first-child',
      title: t('task_lists.table.title'),
      content: t('task_lists.table.content'),
      placement: 'top',
      skipBeacon: true,
      onEnter: () => setMockState((s) => ({ ...s, showMockTaskList: true })),
      onExit: () => setMockState((s) => ({ ...s, showMockTaskList: false })),
    },
    // ─── Offboarding tab ───────────────────────────────────────────────────
    {
      target: 'body',
      placement: 'center',
      skipBeacon: true,
      title: t('task_lists.offboarding_intro.title'),
      content: t('task_lists.offboarding_intro.content'),
      onEnter: () => switchTab('offboarding'),
    },
    {
      target: '[data-tour="task-lists-table"] tbody tr:first-child',
      title: t('task_lists.offboarding_table.title'),
      content: t('task_lists.offboarding_table.content'),
      placement: 'top',
      skipBeacon: true,
      onEnter: () => setMockState((s) => ({ ...s, showMockTaskList: true })),
      onExit: () => setMockState((s) => ({ ...s, showMockTaskList: false })),
    },
    // ─── Tasks tab ─────────────────────────────────────────────────────────
    {
      target: 'body',
      placement: 'center',
      skipBeacon: true,
      title: t('tasks_tab.intro.title'),
      content: t('tasks_tab.intro.content'),
      onEnter: () => switchTab('tasks'),
    },
    {
      target: '[data-tour="tasks-tab-search"]',
      title: t('tasks_tab.search.title'),
      content: t('tasks_tab.search.content'),
      placement: 'bottom',
      skipBeacon: true,
    },
    {
      target: '[data-tour="tasks-tab-filters-panel"]',
      title: t('tasks_tab.filters.title'),
      content: t('tasks_tab.filters.content'),
      placement: 'left',
      skipBeacon: true,
      spotlightPadding: 0,
      onEnter: () => setMockState((s) => ({ ...s, showFilters: true })),
      onExit: () => setMockState((s) => ({ ...s, showFilters: false })),
    },
    {
      target: '[data-tour="tasks-tab-table"] tbody tr:first-child',
      title: t('tasks_tab.table.title'),
      content: t('tasks_tab.table.content'),
      placement: 'top',
      skipBeacon: true,
      onEnter: () => setMockState((s) => ({ ...s, showMockTask: true })),
      onExit: () => setMockState((s) => ({ ...s, showMockTask: false })),
    },
  ],
});

// ─── Edit Task List (detail page) ──────────────────────────────────────────────
export type EditTaskListTourMock = Record<string, unknown> & {
  showMockTask: boolean;
};

export const editTaskListTourVariant: TourVariant<EditTaskListTourMock> = {
  tourId: 'edit-task-list',
  initialMockState: { showMockTask: false },
  buildSteps: (setMockState, t) => [
    {
      target: '[data-tour="edit-task-list-header"]',
      title: t('edit_task_list.header.title'),
      content: t('edit_task_list.header.content'),
      placement: 'bottom',
      skipBeacon: true,
    },
    {
      target: '[data-tour="edit-task-list-add-task"]',
      title: t('edit_task_list.add_task.title'),
      content: t('edit_task_list.add_task.content'),
      placement: 'left',
      skipBeacon: true,
    },
    {
      target: '[data-tour="edit-task-list-first-task"]',
      title: t('edit_task_list.tasks.title'),
      content: t('edit_task_list.tasks.content'),
      placement: 'top',
      skipBeacon: true,
      onEnter: () => setMockState({ showMockTask: true }),
    },
    {
      target: '[data-tour="edit-task-list-child-task"]',
      title: t('edit_task_list.prerequisite.title'),
      content: t('edit_task_list.prerequisite.content'),
      placement: 'top',
      skipBeacon: true,
      onExit: () => setMockState({ showMockTask: false }),
    },
  ],
};

// ─── Admin Assigned Tasks ──────────────────────────────────────────────────────

export type AdminAssignedTasksTourMock = Record<string, unknown>;

export const adminAssignedTasksTourVariant: TourVariant<AdminAssignedTasksTourMock> =
  {
    tourId: 'admin-assigned-tasks',
    initialMockState: {},
    buildSteps: (_setMockState, t) => [
      {
        target: '[data-tour="assigned-tasks-stats"]',
        title: t('admin_assigned_tasks.stats.title'),
        content: t('admin_assigned_tasks.stats.content'),
        placement: 'bottom',
        skipBeacon: true,
      },
      {
        target: '[data-tour="assigned-tasks-filters"]',
        title: t('admin_assigned_tasks.filters.title'),
        content: t('admin_assigned_tasks.filters.content'),
        placement: 'bottom',
        skipBeacon: true,
      },
      {
        target: '[data-tour="assigned-tasks-groups"] li:first-of-type',
        title: t('admin_assigned_tasks.groups.title'),
        content: t('admin_assigned_tasks.groups.content'),
        placement: 'top',
        skipBeacon: true,
      },
    ],
  };

// ─── Employee Boarding (onboarding + offboarding tabs) ─────────────────────────

export type EmployeeBoardingTourMock = Record<string, unknown>;

export const buildEmployeeBoardingTourVariant = (
  type: EmployeeFlowType
): TourVariant<EmployeeBoardingTourMock> => ({
  tourId: `employee-${type}`,
  initialMockState: {},

  buildSteps: (_setMockState, t) => [
    {
      target: '[data-tour="boarding-header"]',
      title: t(`employee_boarding.header.title.${type}`),
      content: t(`employee_boarding.header.content.${type}`),
      placement: 'bottom',
      skipBeacon: true,
    },
    {
      target: '[data-tour="boarding-add-tasks"]',
      title: t('employee_boarding.add_tasks.title'),
      content: t('employee_boarding.add_tasks.content'),
      placement: 'left',
      skipBeacon: true,
    },
    {
      target: '[data-tour="boarding-add-task"]',
      title: t('employee_boarding.add_task.title'),
      content: t('employee_boarding.add_task.content'),
      placement: 'left',
      skipBeacon: true,
    },
    {
      target:
        '[data-tour="boarding-task-groups"] [data-tour="boarding-task-group-item"]',
      title: t('employee_boarding.task_groups.title'),
      content: t('employee_boarding.task_groups.content'),
      placement: 'top',
      skipBeacon: true,
    },
    {
      target: '[data-tour="boarding-complete"]',
      title: t('employee_boarding.complete.title'),
      content: t('employee_boarding.complete.content'),
      placement: 'left',
      skipBeacon: true,
    },
  ],
});

// ─── Task Detail (single company task) ─────────────────────────────────────────
// Bound to a REAL task, not mock data, so the steps branch on this task's
// actual assignment mode / approval setting / occurrence count instead of
// walking through every variant — each task only ever shows one of them.
export const buildTaskDetailTourVariant = (
  mode: UIAssignmentMode,
  requiresApproval: boolean,
  hasMultipleOccurrences: boolean,
  setActiveTab: (tab: string) => void
): TourVariant => ({
  tourId: 'task-detail',
  initialMockState: {},
  buildSteps: (_, t) => [
    {
      target: '[data-tour="task-detail-header"]',
      title: t('task_detail.header.title'),
      content: t('task_detail.header.content'),
      placement: 'bottom' as const,
      skipBeacon: true,
      onEnter: () => setActiveTab('details'),
    },
    {
      target: '[data-tour="task-detail-tabs"]',
      title: t('task_detail.tabs.title'),
      content: t(`task_detail.tabs.content_${mode}`),
      placement: 'bottom' as const,
      skipBeacon: true,
    },
    {
      target: 'body' as const,
      placement: 'center' as const,
      skipBeacon: true,
      title: t(`task_detail.assignees_intro.${mode}.title`),
      content: t(`task_detail.assignees_intro.${mode}.content`),
      onEnter: () => setActiveTab('assignees'),
    },
    {
      target: '[data-tour="task-detail-assignees"]',
      title: t(`task_detail.assignees.${mode}.title`),
      content: requiresApproval
        ? t(`task_detail.assignees.${mode}.content_approval`)
        : t(`task_detail.assignees.${mode}.content_simple`),
      placement: 'top' as const,
      skipBeacon: true,
    },
    // Group discussion tab only exists for group-mode tasks — single/separate
    // assignees each get a private thread instead, reachable from their row.
    ...(mode === 'group'
      ? [
          {
            target: 'body' as const,
            placement: 'center' as const,
            skipBeacon: true,
            title: t('task_detail.communication_intro.title'),
            content: t('task_detail.communication_intro.content'),
            onEnter: () => setActiveTab('communication'),
          },
          {
            target: '[data-tour="task-detail-communication"]',
            title: t('task_detail.communication.title'),
            content: t('task_detail.communication.content'),
            placement: 'top' as const,
            skipBeacon: true,
          },
        ]
      : []),
    {
      target: 'body' as const,
      placement: 'center' as const,
      skipBeacon: true,
      title: t('task_detail.activity_intro.title'),
      content: t('task_detail.activity_intro.content'),
      onEnter: () => setActiveTab('activity'),
    },
    {
      target: '[data-tour="task-detail-activity"]',
      title: t('task_detail.activity.title'),
      content: t('task_detail.activity.content'),
      placement: 'top' as const,
      skipBeacon: true,
    },
    {
      target: '[data-tour="task-detail-sidebar"]',
      title: t('task_detail.sidebar.title'),
      content: hasMultipleOccurrences
        ? t('task_detail.sidebar.content_recurring')
        : t('task_detail.sidebar.content_simple'),
      placement: 'left' as const,
      skipBeacon: true,
    },
  ],
});

// ─── Benefit Plans (list page) ─────────────────────────────────────────────────

export type BenefitPlansTourMock = Record<string, unknown> & {
  showMockPlan: boolean;
  showFilters: boolean;
};

export const benefitPlansTourVariant: TourVariant<BenefitPlansTourMock> = {
  tourId: 'benefit-plans',
  initialMockState: { showMockPlan: false, showFilters: false },
  buildSteps: (setMockState, t) => [
    {
      target: '[data-tour="benefit-plans-header"]',
      title: t('benefit_plans.header.title'),
      content: t('benefit_plans.header.content'),
      placement: 'bottom',
      skipBeacon: true,
    },
    {
      target: '[data-tour="benefit-plans-search"]',
      title: t('benefit_plans.search.title'),
      content: t('benefit_plans.search.content'),
      placement: 'bottom',
      skipBeacon: true,
    },
    {
      target: '[data-tour="benefit-plans-filters-panel"]',
      title: t('benefit_plans.filters.title'),
      content: t('benefit_plans.filters.content'),
      placement: 'left',
      skipBeacon: true,
      spotlightPadding: 0,
      onEnter: () => setMockState((s) => ({ ...s, showFilters: true })),
      onExit: () => setMockState((s) => ({ ...s, showFilters: false })),
    },
    {
      target: '[data-tour="benefit-plans-table"] tbody tr:first-child',
      title: t('benefit_plans.table.title'),
      content: t('benefit_plans.table.content'),
      placement: 'top',
      skipBeacon: true,
      onEnter: () => setMockState((s) => ({ ...s, showMockPlan: true })),
      onExit: () => setMockState((s) => ({ ...s, showMockPlan: false })),
    },
  ],
};

// ─── Notes (Admin tab) ──────────────────────────────────────────────────────
export type NotesAdminTourMock = Record<string, unknown>;

export const notesAdminTourVariant: TourVariant<NotesAdminTourMock> = {
  tourId: 'notes-admin',
  initialMockState: {},
  buildSteps: (_setMockState, t) => [
    {
      target: '[data-tour="notes-header"]',
      title: t('notes.header.title'),
      content: t('notes.header.content'),
      placement: 'bottom',
      skipBeacon: true,
    },
    {
      target: '[data-tour="notes-search"]',
      title: t('notes.search.title'),
      content: t('notes.search.content'),
      placement: 'bottom',
      skipBeacon: true,
    },
    {
      target: '[data-tour="notes-admin-categories"]',
      title: t('notes.admin.categories.title'),
      content: t('notes.admin.categories.content'),
      placement: 'bottom',
      skipBeacon: true,
    },
    {
      target: '[data-tour="notes-admin-add"]',
      title: t('notes.admin.add.title'),
      content: t('notes.admin.add.content'),
      placement: 'bottom',
      skipBeacon: true,
    },
    {
      target: '[data-tour="notes-admin-grid"] > div:first-child',
      title: t('notes.admin.grid.title'),
      content: t('notes.admin.grid.content'),
      placement: 'top',
      skipBeacon: true,
    },
    {
      target:
        '[data-tour="notes-admin-grid"] > div:first-child [data-tour="note-pin-button"]',
      title: t('notes.admin.pin.title'),
      content: t('notes.admin.pin.content'),
      placement: 'bottom',
      skipBeacon: true,
    },
    {
      target:
        '[data-tour="notes-admin-grid"] > div:first-child [data-tour="note-share-button"]',
      title: t('notes.admin.share.title'),
      content: t('notes.admin.share.content'),
      placement: 'top',
      skipBeacon: true,
    },
  ],
};

// ─── Notes (Employee tab) ───────────────────────────────────────────────────
export type NotesEmployeeTourMock = Record<string, unknown>;

export const buildNotesEmployeeTourVariant = (
  selectFirstEmployee: () => void
): TourVariant<NotesEmployeeTourMock> => ({
  tourId: 'notes-employee',
  initialMockState: {},
  buildSteps: (_setMockState, t) => [
    {
      target: 'body',
      placement: 'center',
      skipBeacon: true,
      title: t('notes.employee_tab.intro.title'),
      content: t('notes.employee_tab.intro.content'),
    },
    {
      target: '[data-tour="notes-employee-sidebar"]',
      title: t('notes.employee_tab.sidebar.title'),
      content: t('notes.employee_tab.sidebar.content'),
      placement: 'right',
      skipBeacon: true,
    },
    {
      target: '[data-tour="notes-employee-sidebar-search"]',
      title: t('notes.employee_tab.sidebar_search.title'),
      content: t('notes.employee_tab.sidebar_search.content'),
      placement: 'right',
      skipBeacon: true,
      onExit: () => selectFirstEmployee(),
    },
    {
      target: '[data-tour="notes-employee-add"]',
      title: t('notes.employee_tab.add.title'),
      content: t('notes.employee_tab.add.content'),
      placement: 'bottom',
      skipBeacon: true,
    },
    {
      target: '[data-tour="notes-employee-grid"] > div > div:first-child',
      title: t('notes.employee_tab.grid.title'),
      content: t('notes.employee_tab.grid.content'),
      placement: 'top',
      skipBeacon: true,
    },
  ],
});

// ─── Notes (Announcements tab) ──────────────────────────────────────────────
export type NotesAnnouncementsTourMock = Record<string, unknown>;

export const notesAnnouncementsTourVariant: TourVariant<NotesAnnouncementsTourMock> =
  {
    tourId: 'notes-announcements',
    initialMockState: {},
    buildSteps: (_setMockState, t) => [
      {
        target: 'body',
        placement: 'center',
        skipBeacon: true,
        title: t('notes.announcements.intro.title'),
        content: t('notes.announcements.intro.content'),
      },
      {
        target: '[data-tour="notes-announcements-filters"]',
        title: t('notes.announcements.filters.title'),
        content: t('notes.announcements.filters.content'),
        placement: 'bottom',
        skipBeacon: true,
      },
      {
        target: '[data-tour="notes-announcements-add"]',
        title: t('notes.announcements.add.title'),
        content: t('notes.announcements.add.content'),
        placement: 'bottom',
        skipBeacon: true,
      },
      {
        target: '[data-tour="notes-announcements-list"] > div:first-child',
        title: t('notes.announcements.list.title'),
        content: t('notes.announcements.list.content'),
        placement: 'top',
        skipBeacon: true,
      },
    ],
  };
